export const SIGNAL_METADATA_NAMESPACE = "pl_signals";
export const SIGNAL_METADATA_KEY_PREFIX = `${SIGNAL_METADATA_NAMESPACE}.`;
export const SIGNAL_ANY_TRIGGERED_FILTER_KEY = `${SIGNAL_METADATA_NAMESPACE}.__any_triggered__`;

const SIGNAL_ID_RE = /^pl_signals\.signal_(\d+)(?:\.|$)/;
const SIGNAL_PARENT_KEY_RE = /^pl_signals\.signal_(\d+)$/;

export interface SignalMetadataResult {
  signalId: number;
  label: boolean;
  reasoning: string | null;
}

export interface SidebarSignalBubble {
  signalId: number;
  name: string;
  isTrue: boolean;
}

export interface RequestSignalBubble extends SidebarSignalBubble {
  justification: string | null;
}

export interface SignalStructuredFilterDetail {
  field: "signal";
  nestedKey: string;
  nestedKeyDisplay: string;
  value: string;
  valueDisplay: string;
}

export const getSignalBubbleClassName = (isActive = false): string =>
  isActive ? "bg-gray-200 text-gray-700" : "bg-gray-100 text-gray-600";

export type SignalMetadataInput = null | [null] | Record<string, string>[] | undefined;

export const parseSignalIdFromMetadataKey = (key: string): number | null => {
  const match = key.trim().match(SIGNAL_ID_RE);
  if (!match) {
    return null;
  }
  return Number(match[1]);
};

export const signalMetadataParentKey = (signalId: number): string =>
  `${SIGNAL_METADATA_KEY_PREFIX}signal_${signalId}`;

export const isSignalMetadataKey = (key: string): boolean =>
  key.trim().startsWith(SIGNAL_METADATA_KEY_PREFIX);

const parseSignalMetadataPayload = (
  raw: string,
): { label: boolean; reasoning: string | null } | null => {
  const trimmed = raw.trim();
  if (trimmed.startsWith("{")) {
    try {
      const parsed: unknown = JSON.parse(trimmed);
      if (typeof parsed === "object" && parsed !== null && "label" in parsed) {
        const labelValue = String((parsed as { label: unknown }).label).toLowerCase();
        let label: boolean | null = null;
        if (labelValue === "true") {
          label = true;
        } else if (labelValue === "false") {
          label = false;
        }
        if (label === null) {
          return null;
        }

        const reasoningValue = (parsed as { reasoning?: unknown }).reasoning;
        const reasoning =
          typeof reasoningValue === "string" && reasoningValue.trim()
            ? reasoningValue.trim()
            : null;
        return { label, reasoning };
      }
    } catch {
      return null;
    }
    return null;
  }

  const normalized = trimmed.toLowerCase();
  if (normalized === "true") {
    return { label: true, reasoning: null };
  }
  if (normalized === "false") {
    return { label: false, reasoning: null };
  }
  return null;
};

export const extractSignalResultsFromMetadata = (
  metadata: SignalMetadataInput,
): SignalMetadataResult[] => {
  if (!metadata || !Array.isArray(metadata)) {
    return [];
  }

  const results: SignalMetadataResult[] = [];
  const seenSignalIds = new Set<number>();

  for (const item of metadata) {
    if (!item || typeof item !== "object") {
      continue;
    }

    for (const [key, value] of Object.entries(item)) {
      const match = key.trim().match(SIGNAL_PARENT_KEY_RE);
      if (!match || typeof value !== "string") {
        continue;
      }

      const signalId = Number(match[1]);
      const parsed = parseSignalMetadataPayload(value);
      if (parsed === null || seenSignalIds.has(signalId)) {
        continue;
      }

      seenSignalIds.add(signalId);
      results.push({
        signalId,
        label: parsed.label,
        reasoning: parsed.reasoning,
      });
    }
  }

  return results.sort((left, right) => left.signalId - right.signalId);
};

export const hydrateSidebarSignalBubbles = (
  metadata: SignalMetadataInput,
  signalNamesById: Map<number, string>,
): SidebarSignalBubble[] =>
  extractSignalResultsFromMetadata(metadata)
    .flatMap(({ signalId, label }) => {
      const name = signalNamesById.get(signalId);
      if (!name) {
        return [];
      }
      return [{ signalId, name, isTrue: label }];
    })
    .sort((left, right) => left.name.localeCompare(right.name));

export const hydrateRequestSignalBubbles = (
  metadata: SignalMetadataInput,
  signalNamesById: Map<number, string>,
): RequestSignalBubble[] =>
  extractSignalResultsFromMetadata(metadata)
    .flatMap(({ signalId, label, reasoning }) => {
      const name = signalNamesById.get(signalId);
      if (!name) {
        return [];
      }
      return [{ signalId, name, isTrue: label, justification: reasoning }];
    })
    .sort((left, right) => left.name.localeCompare(right.name));

export const formatSignalLabelValue = (value: string): string => {
  const normalized = value.trim().toLowerCase();
  if (normalized === "true") {
    return "True";
  }
  if (normalized === "false") {
    return "False";
  }
  return value;
};

export const buildSignalStructuredFilterDetail = (
  signalId: number,
  signalName: string,
  isTrue: boolean,
): SignalStructuredFilterDetail => ({
  field: "signal",
  nestedKey: signalMetadataParentKey(signalId),
  nestedKeyDisplay: signalName,
  value: isTrue ? "true" : "false",
  valueDisplay: formatSignalLabelValue(isTrue ? "true" : "false"),
});

export const buildNoSignalStructuredFilterDetail = (): SignalStructuredFilterDetail => ({
  field: "signal",
  nestedKey: SIGNAL_ANY_TRIGGERED_FILTER_KEY,
  nestedKeyDisplay: "No Signal triggered",
  value: "false",
  valueDisplay: "No Signal triggered",
});

export interface SignalKeySuggestionInput {
  value: string;
  count?: number;
}

export interface HydratedSignalKeySuggestion {
  value: string;
  label: string;
  count?: number;
}

export const hydrateSignalKeySuggestions = (
  suggestions: SignalKeySuggestionInput[],
  signalNamesById: Map<number, string>,
  namePrefix = "",
): HydratedSignalKeySuggestion[] => {
  const bySignalId = new Map<number, HydratedSignalKeySuggestion>();
  const loweredPrefix = namePrefix.trim().toLowerCase();

  for (const suggestion of suggestions) {
    if (!isSignalMetadataKey(suggestion.value)) {
      continue;
    }

    const signalId = parseSignalIdFromMetadataKey(suggestion.value);
    if (signalId === null) {
      continue;
    }

    const name = signalNamesById.get(signalId);
    if (!name) {
      continue;
    }

    if (loweredPrefix && !name.toLowerCase().includes(loweredPrefix)) {
      continue;
    }

    const parentKey = signalMetadataParentKey(signalId);
    const existing = bySignalId.get(signalId);
    if (!existing || (suggestion.count ?? 0) > (existing.count ?? 0)) {
      bySignalId.set(signalId, {
        value: parentKey,
        label: name,
        count: suggestion.count,
      });
    }
  }

  return [...bySignalId.values()].sort((left, right) => {
    const countDelta = (right.count ?? 0) - (left.count ?? 0);
    if (countDelta !== 0) {
      return countDelta;
    }
    return left.label.localeCompare(right.label);
  });
};
